import { useMutation, useQueryClient } from '@tanstack/react-query';

import { pastExportsApi } from '@/features/DataHub/api/pastExports.api';
import { pastExportsKeys } from '@/features/DataHub/queries/keys/pastExports.keys';
import type { ExportItemType } from '@/features/DataHub/types/pastExports';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';

const snackbarKey = 'deleteExport';

export const useDeletePastExport = () => {
  const queryClient = useQueryClient();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbarNotifications();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await pastExportsApi.deletePastExport(id);
      return response;
    },
    mutationKey: [...pastExportsKeys.all, 'delete'] as const,
    onMutate: async (id: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: pastExportsKeys.lists() });

      // Snapshot previous value
      const previousExports = queryClient.getQueryData<ExportItemType[]>(pastExportsKeys.lists());

      // Optimistically remove the export
      queryClient.setQueryData<ExportItemType[]>(pastExportsKeys.lists(), old =>
        old?.filter(item => item.id !== id)
      );

      return { previousExports };
    },
    onSuccess: (_response, id) => {
      showSuccessSnackbar('Past export has been deleted', snackbarKey);
      queryClient.removeQueries({ queryKey: pastExportsKeys.detail(id) });

      // Sync with server after delay
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: pastExportsKeys.lists() });
      }, 5000);
    },
    onError: (_error, _id, context) => {
      // Rollback on error
      if (context?.previousExports) {
        queryClient.setQueryData(pastExportsKeys.lists(), context.previousExports);
      }
      showErrorSnackbar('Error deleting past export', 2000, snackbarKey);
    }
  });
};
